import { useEffect, useRef, useState } from 'react';

export default function Hero() {
  const [visible, setVisible] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Slight delay so the intro exit transition finishes before hero animates in
    const t = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(t);
  }, []);

  const scrollToCollection = () => {
    const equipmentSection = document.getElementById('equipment-section');
    if (equipmentSection) {
      equipmentSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const headline1 = 'Melbourne\'s Premier Event Hire';
  const headline2 = '& Bar Equipment Specialists';

  return (
    <div ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">

      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/5227ff6ed3dcbbfc69390658c799b7b0.jpg"
          alt="Premium Event Setup"
          className={`w-full h-full object-cover object-center transition-all duration-[2000ms] ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}`}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70" />
      </div>

      {/* Animated background grid — mirrors intro */}
      <div
        className={`absolute inset-0 transition-opacity duration-[1500ms] ${visible ? 'opacity-[0.06]' : 'opacity-0'}`}
        style={{
          backgroundImage: 'linear-gradient(rgba(212,175,55,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.4) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      {/* Radial gold glow — centre */}
      <div
        className={`absolute w-[700px] h-[700px] rounded-full pointer-events-none transition-all duration-[2000ms] ${visible ? 'opacity-100' : 'opacity-0'}`}
        style={{
          background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0.04) 40%, transparent 70%)',
          filter: 'blur(60px)',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />

      {/* Orbiting rings — mirrors intro */}
      <div
        className={`absolute w-[420px] h-[420px] rounded-full border border-amber-400/15 pointer-events-none transition-all duration-[1200ms] ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}
        style={{ animation: 'heroSpin 10s linear infinite', left: '50%', top: '50%', marginLeft: '-210px', marginTop: '-210px' }}
      />
      <div
        className={`absolute w-[560px] h-[560px] rounded-full border border-amber-300/08 pointer-events-none transition-all duration-[1600ms] ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}
        style={{ animation: 'heroSpinReverse 16s linear infinite', left: '50%', top: '50%', marginLeft: '-280px', marginTop: '-280px' }}
      />

      {/* Floating gold particles — mirrors intro */}
      {Array.from({ length: 18 }).map((_, i) => {
        const size = (i % 3) + 2;
        const left = ((i * 37 + 11) % 90) + 5;
        const top = ((i * 53 + 7) % 80) + 10;
        const dur = (i % 4) + 3;
        const delay = (i % 5) * 0.4;
        return (
          <div
            key={i}
            className={`absolute rounded-full pointer-events-none transition-opacity duration-[2000ms] ${visible ? 'opacity-100' : 'opacity-0'}`}
            style={{
              width: `${size}px`,
              height: `${size}px`,
              background: `rgba(212,175,55,${0.3 + (i % 4) * 0.15})`,
              left: `${left}%`,
              top: `${top}%`,
              animation: `heroFloat ${dur}s ease-in-out infinite`,
              animationDelay: `${delay}s`,
              boxShadow: '0 0 6px rgba(212,175,55,0.5)',
              transitionDelay: `${i * 60}ms`,
            }}
          />
        );
      })}

      {/* Corner decorative lines — mirrors intro */}
      {[
        'top-8 left-8 border-t border-l',
        'top-8 right-8 border-t border-r',
        'bottom-20 left-8 border-b border-l',
        'bottom-20 right-8 border-b border-r',
      ].map((cls, i) => (
        <div
          key={i}
          className={`absolute w-14 h-14 border-amber-400/30 ${cls} transition-all duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}
          style={{ transitionDelay: `${400 + i * 120}ms` }}
        />
      ))}

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-32 text-center w-full">

        {/* Eyebrow label */}
        <div
          className={`inline-flex items-center gap-2 mb-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: '300ms' }}
        >
          <div className="w-8 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.8))' }} />
          <span
            className="text-xs tracking-[0.4em] uppercase font-light"
            style={{ color: 'rgba(212,175,55,0.85)', fontFamily: "'Montserrat', sans-serif" }}
          >
            Trusted by 500+ Events Across Melbourne
          </span>
          <div className="w-8 h-px" style={{ background: 'linear-gradient(90deg, rgba(212,175,55,0.8), transparent)' }} />
        </div>

        {/* Headline — letter by letter, mirrors intro */}
        <h1 className="mb-6 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          <div className="flex flex-wrap justify-center gap-x-[0.25em] mb-2">
            {headline1.split(' ').map((word, wi) => (
              <span key={wi} className="flex">
                {word.split('').map((char, ci) => {
                  const idx = headline1.split(' ').slice(0, wi).join(' ').length + (wi > 0 ? 1 : 0) + ci;
                  return (
                    <span
                      key={ci}
                      className={`text-5xl md:text-7xl font-bold text-white transition-all duration-500 ${visible ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-6 blur-sm'}`}
                      style={{ transitionDelay: `${500 + idx * 28}ms` }}
                    >
                      {char}
                    </span>
                  );
                })}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-x-[0.25em]">
            {headline2.split(' ').map((word, wi) => (
              <span key={wi} className="flex">
                {word.split('').map((char, ci) => {
                  const base = headline1.length + 1;
                  const idx = base + headline2.split(' ').slice(0, wi).join(' ').length + (wi > 0 ? 1 : 0) + ci;
                  return (
                    <span
                      key={ci}
                      className={`text-5xl md:text-7xl font-bold transition-all duration-500 ${visible ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-6 blur-sm'}`}
                      style={{
                        transitionDelay: `${500 + idx * 22}ms`,
                        background: 'linear-gradient(135deg, #d4af37 0%, #f5e27a 40%, #d4af37 70%, #b8960c 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.35))',
                      }}
                    >
                      {char}
                    </span>
                  );
                })}
              </span>
            ))}
          </div>
        </h1>

        {/* Sub-headline — clear value proposition */}
        <p
          className={`text-xl md:text-2xl text-gray-200 mb-4 max-w-3xl mx-auto font-light transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: '1200ms', fontFamily: "'Montserrat', sans-serif" }}
        >
          Premium glassware, bar setups & event essentials delivered with precision across Melbourne
        </p>

        {/* Trust stat line */}
        <p
          className={`text-base md:text-lg text-amber-200/90 mb-10 max-w-2xl mx-auto font-medium transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: '1300ms', fontFamily: "'Montserrat', sans-serif" }}
        >
          Fully insured • Same-day quotes • Reliable delivery & collection
        </p>

        {/* CTA Buttons — stronger hierarchy */}
        <div
          className={`flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: '1400ms' }}
        >
          <a
            href="/contact"
            className="px-12 py-5 text-black font-bold text-lg rounded-lg transition-all duration-300 shadow-2xl hover:shadow-amber-500/60 whitespace-nowrap cursor-pointer hover:scale-105 hover:-translate-y-1"
            style={{ background: 'linear-gradient(135deg, #d4af37 0%, #f5e27a 50%, #d4af37 100%)' }}
          >
            Book Now — Get Instant Quote
          </a>
          <button
            onClick={scrollToCollection}
            className="px-10 py-5 bg-white/10 backdrop-blur-sm border-2 border-amber-400/60 hover:border-amber-300 hover:bg-white/20 text-white font-semibold text-lg rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            View Equipment Catalogue
          </button>
        </div>

        {/* Trust Badges — prominent above fold */}
        <div
          className={`grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl mx-auto mb-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: '1600ms' }}
        >
          {[
            { icon: 'ri-calendar-check-line', label: '500+ Events', sublabel: 'Successfully Delivered' },
            { icon: 'ri-shield-check-line', label: 'Fully Insured', sublabel: '$20M Public Liability' },
            { icon: 'ri-truck-line', label: 'Melbourne Wide', sublabel: 'Delivery & Collection' },
            { icon: 'ri-customer-service-2-line', label: '24/7 Support', sublabel: 'Event Day Assistance' },
          ].map((badge, i) => (
            <div
              key={i}
              className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-amber-400/20 transition-all duration-500 hover:bg-white/10 hover:border-amber-400/40 hover:scale-105 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: `${1700 + i * 100}ms` }}
            >
              <i className={`${badge.icon} text-3xl text-amber-400`} />
              <span className="text-base md:text-lg font-bold text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {badge.label}
              </span>
              <span className="text-xs md:text-sm text-gray-300 text-center" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {badge.sublabel}
              </span>
            </div>
          ))}
        </div>

        {/* Partner logos / venue trust signals */}
        <div
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: '2000ms' }}
        >
          <p className="text-xs tracking-[0.3em] uppercase text-amber-200/60 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Trusted by Melbourne's Leading Venues
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-70">
            {[
              'Crown Melbourne',
              'The Langham',
              'Zinc Federation Square',
              'Luminare',
              'Carousel',
            ].map((venue, i) => (
              <span
                key={i}
                className="text-sm font-medium text-white/80 px-4 py-2 rounded-full bg-white/5 border border-amber-400/10"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {venue}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all duration-700 ${visible ? 'opacity-70' : 'opacity-0'}`}
        style={{ transitionDelay: '2200ms', animation: visible ? 'heroBounce 1.5s ease-in-out infinite' : 'none' }}
      >
        <i className="ri-arrow-down-line text-3xl text-white/70" />
      </div>

      {/* Keyframes */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;700&family=Montserrat:wght@300;400;600;700&display=swap');

        @keyframes heroSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes heroSpinReverse {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }
        @keyframes heroFloat {
          0%, 100% { transform: translateY(0px) scale(1); opacity: 0.4; }
          50% { transform: translateY(-18px) scale(1.2); opacity: 0.9; }
        }
        @keyframes heroBounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(8px); }
        }
      `}</style>
    </div>
  );
}